/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import * as z from "zod";
import type { ToZod } from "../.kubb/ToZod.ts";
import type { UpdatePersonRequestViewModel } from "../types/UpdatePersonRequestViewModel.ts";
import { createAddressRequestViewModelSchema } from "./createAddressRequestViewModelSchema.ts";
import { eAcademicEducationSchema } from "./eAcademicEducationSchema.ts";
import { eExperienceLevelSchema } from "./eExperienceLevelSchema.ts";
import { eGenderSchema } from "./eGenderSchema.ts";
import { eMartialStatusSchema } from "./eMartialStatusSchema.ts";
import { eServiceTimeSchema } from "./eServiceTimeSchema.ts";

export const updatePersonRequestViewModelSchema = z.object({
  firstName: z.string().describe("Primeiro nome da pessoa.").nullish(),
  lastName: z.string().describe("Sobrenome da pessoa.").nullish(),
  email: z.string().describe("Endereço de e-mail da pessoa.").nullish(),
  code: z
    .string()
    .describe("Código de identificação da pessoa no sistema. Opcional.")
    .nullish(),
  alias: z
    .string()
    .describe("Apelido ou nome alternativo da pessoa. Opcional.")
    .nullish(),
  birthDate: z
    .string()
    .datetime()
    .describe("Data de nascimento da pessoa. Opcional.")
    .nullish(),
  gender: z.optional(
    z
      .lazy(() => eGenderSchema)
      .describe("1 = Male | 2 = Female | 3 = IDontWantToAnswer | "),
  ),
  martialStatus: z.optional(
    z
      .lazy(() => eMartialStatusSchema)
      .describe("0 = Uninformed | 1 = Married | 2 = Unmarried | "),
  ),
  nationalityId: z
    .number()
    .int()
    .describe("ID da nacionalidade associada à pessoa. Opcional.")
    .nullish(),
  passportNumber: z
    .string()
    .describe("Número do passaporte da pessoa. Opcional.")
    .nullish(),
  idCountryNumber: z
    .string()
    .describe(
      "Número do documento de identidade estrangeiro da pessoa. Opcional.",
    )
    .nullish(),
  cpf: z.string().describe("CPF da pessoa. Opcional.").nullish(),
  rg: z.string().describe("RG da pessoa. Opcional.").nullish(),
  dispatcherOrgan: z
    .string()
    .describe("Órgão emissor do RG. Opcional.")
    .nullish(),
  dispatcherOrganState: z
    .string()
    .describe("Estado do órgão emissor do RG. Opcional.")
    .nullish(),
  dateDispatch: z
    .string()
    .datetime()
    .describe("Data de emissão do RG. Opcional.")
    .nullish(),
  celPhone: z
    .string()
    .describe("Número de celular da pessoa. Opcional.")
    .nullish(),
  homePhone: z
    .string()
    .describe("Número de telefone residencial da pessoa. Opcional.")
    .nullish(),
  workPhone: z
    .string()
    .describe("Número de telefone comercial da pessoa. Opcional.")
    .nullish(),
  profession: z
    .string()
    .describe("Nome da profissão da pessoa (texto livre). Opcional.")
    .nullish(),
  professionId: z
    .number()
    .int()
    .describe("ID da profissão associada à pessoa. Opcional.")
    .nullish(),
  company: z
    .string()
    .describe("Nome da empresa onde a pessoa trabalha (texto livre). Opcional.")
    .nullish(),
  associatedCompanyId: z
    .number()
    .int()
    .describe("ID da empresa associada à pessoa. Opcional.")
    .nullish(),
  academicEducation: z.optional(
    z
      .lazy(() => eAcademicEducationSchema)
      .describe(
        "1 = UndergraduateDegree | 2 = Technologist | 3 = PostgraduateDegree | 4 = MasterDegree | 5 = DoctorateDegree | 6 = BachelorDegree | 7 = HighSchool | 8 = Specialist | ",
      ),
  ),
  academicInstitutionId: z
    .number()
    .int()
    .describe("ID da instituição de ensino associada à pessoa. Opcional.")
    .nullish(),
  academicCourseName: z
    .string()
    .describe("Nome do curso acadêmico da pessoa. Opcional.")
    .nullish(),
  completionDate: z
    .string()
    .datetime()
    .describe("Data de conclusão do curso acadêmico. Opcional.")
    .nullish(),
  mothersName: z
    .string()
    .describe("Nome da mãe da pessoa. Opcional.")
    .nullish(),
  fathersName: z
    .string()
    .describe("Nome do pai da pessoa. Opcional.")
    .nullish(),
  spouseName: z
    .string()
    .describe("Nome do cônjuge da pessoa. Opcional.")
    .nullish(),
  miniCurriculum: z
    .string()
    .describe("Mini currículo ou resumo profissional da pessoa. Opcional.")
    .nullish(),
  urlLinkedin: z
    .string()
    .describe("URL do perfil do LinkedIn da pessoa. Opcional.")
    .nullish(),
  urlLattes: z
    .string()
    .describe("URL do currículo Lattes da pessoa. Opcional.")
    .nullish(),
  observation: z
    .string()
    .describe("Observações gerais sobre a pessoa. Opcional.")
    .nullish(),
  isStudent: z
    .boolean()
    .describe("Indica se a pessoa possui vínculo como aluno. Opcional.")
    .nullish(),
  isMonitor: z
    .boolean()
    .describe("Indica se a pessoa possui vínculo como monitor. Opcional.")
    .nullish(),
  isEmployee: z
    .boolean()
    .describe("Indica se a pessoa possui vínculo como funcionário. Opcional.")
    .nullish(),
  isTeachingStaff: z
    .boolean()
    .describe("Indica se a pessoa faz parte do corpo docente. Opcional.")
    .nullish(),
  receiveSms: z
    .boolean()
    .describe(
      "Define se a pessoa aceita receber notificações por SMS. Opcional.",
    )
    .nullish(),
  receiveMarketing: z
    .boolean()
    .describe(
      "Define se a pessoa aceita receber comunicações de marketing. Opcional.",
    )
    .nullish(),
  birthCityId: z
    .number()
    .int()
    .describe("ID da cidade de nascimento associada à pessoa. Opcional.")
    .nullish(),
  birthCityName: z
    .string()
    .describe("Nome da cidade de nascimento da pessoa. Opcional.")
    .nullish(),
  birthStateId: z
    .number()
    .int()
    .describe("ID do estado de nascimento associado à pessoa. Opcional.")
    .nullish(),
  birthStateName: z
    .string()
    .describe("Nome do estado de nascimento da pessoa. Opcional.")
    .nullish(),
  birthCountryId: z
    .number()
    .int()
    .describe("ID do país de nascimento associado à pessoa. Opcional.")
    .nullish(),
  birthCountryName: z
    .string()
    .describe("Nome do país de nascimento da pessoa. Opcional.")
    .nullish(),
  jobPositionId: z
    .number()
    .int()
    .describe("ID do cargo profissional associado à pessoa. Opcional.")
    .nullish(),
  otherJobPosition: z
    .string()
    .describe(
      "Descrição de cargo profissional não listado (texto livre). Opcional.",
    )
    .nullish(),
  businessSectorId: z
    .number()
    .int()
    .describe("ID do setor de atuação associado à pessoa. Opcional.")
    .nullish(),
  otherBusinessSector: z
    .string()
    .describe(
      "Descrição de setor de atuação não listado (texto livre). Opcional.",
    )
    .nullish(),
  serviceTime: z.optional(
    z
      .lazy(() => eServiceTimeSchema)
      .describe(
        "1 = UpTo1Year | 2 = OneToThreeYears | 3 = ThreeToFiveYears | 4 = MoreThanFiveYears | 5 = NotApplicable | ",
      ),
  ),
  experienceLevel: z.optional(
    z
      .lazy(() => eExperienceLevelSchema)
      .describe(
        "1 = Junior | 2 = MidLevel | 3 = Senior | 4 = NotApplicable | ",
      ),
  ),
  selfEmployed: z
    .boolean()
    .describe("Indica se a pessoa é autônoma. Opcional.")
    .nullish(),
  isWorking: z
    .boolean()
    .describe("Indica se a pessoa está atualmente empregada. Opcional.")
    .nullish(),
  isPCD: z
    .boolean()
    .describe("Indica se a pessoa é portadora de deficiência (PCD). Opcional.")
    .nullish(),
  pcdDescription: z
    .string()
    .describe("Descrição da deficiência da pessoa. Opcional.")
    .nullish(),
  useAccessibilityTool: z
    .boolean()
    .describe(
      "Indica se a pessoa utiliza alguma ferramenta de acessibilidade. Opcional.",
    )
    .nullish(),
  accessibilityToolDescription: z
    .string()
    .describe(
      "Descrição da ferramenta de acessibilidade utilizada pela pessoa. Opcional.",
    )
    .nullish(),
  personalAddress: z.optional(
    z.lazy(() => createAddressRequestViewModelSchema),
  ),
  paymentAddress: z.optional(z.lazy(() => createAddressRequestViewModelSchema)),
}) as unknown as ToZod<UpdatePersonRequestViewModel>;
